import React, { useState, useEffect, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import {
    handleGetJitUsersData,
    getModalSwitch,
    setOrderByDirection,
    setOrderByProperty,
    resetState,
    resetScroll,
    showJitOrgSelect
} from '@app/store/actions';
import {
    passlyFormatDate,
    dateIsValidForDisplay,
    DATE_FORMAT,
    DATE_TIME_FORMAT,
    TIME_FORMAT,
    infiniteScrollItemsFetchLimit
} from '@app/helpers';
import {ITableHead, IJitUser, OrderType, Order, FilterEnum, IActiveFilter} from '@app/models';
import { TableCell } from '@material-ui/core';
import { hasOrgPermission } from '@app/helpers/permissions';
import { ManageAccount } from '@app/store/constants';
import {
    IModalData,
    ModalComponent,
    PasslyButton,
    PasslyTable
} from '@app/components';
import { basicService } from '@app/services/basic.service';
import ReserveUserDialog from '@app/pages/jit/dialogs/reserveUser.dialog';
import { jitUsersService } from '@app/services';

interface IJitUsersTableData {
    principalName: string;
    displayName: string;
    lastSignedIn: Date;
    lastExpiry: Date;
    lastExpiryIsExpired: boolean;
    requestorHasReservation: boolean;
    requestorReservationExpiry: Date;
    canReserve: boolean;
    id: string;
    organizationName: string;
}

enum TableHeadEnum {
    displayName = 'displayName',
    principalName = 'principalName',
    organization = 'organizationName',
    lastExpiry = 'lastExpiry',
    myReservation = 'myReservation',
    lastSignedIn = 'lastSignedIn',
    button = 'button'
}

const JitUsersPage = () => {
    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('displayName');
    const [rows, setRows] = useState<IJitUsersTableData[]>([]);

    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedJitOrganization);
    const jitUsers = useSelector((state: any) => state.jitUsers.jitUsers);
    const jitUsersCount = useSelector((state: any) => state.jitUsers.jitUsersCount);
    const [filters, setFilters] = useState<IActiveFilter[]>([]);
    const jitUsersLoading = useSelector((state: any) => state.jitUsers.jitUsersLoading);
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const editRow = (e: any) => {
        basicService.redirect('/directory/users/details/' + e.currentTarget.dataset.id);
    };

    const getComplexLastExpiryCell = (row: any) => {
        return <TableCell>
            {!row.lastExpiryIsExpired && dateIsValidForDisplay(row.lastExpiry)
                ? formatLastExpiry(row.lastExpiry)
                : translation('JIT_AVAILABLE')}
        </TableCell>;
    };

    const getComplexReservationCell = (row: any) => {
        return <TableCell>
            {row.requestorHasReservation
                ? makeDateTimeSpanDivHelper(row.requestorReservationExpiry)
                : translation('NOT_RESERVED')}
        </TableCell>;
    };

    const getComplexButtonCell = (row: any) => {
        return (
            <TableCell>
                {row.requestorHasReservation ? (
                    <PasslyButton
                        fullWidth
                        variant='outlined'
                        onClick={() => {
                            expireUser(row, rows);
                        }}
                    >
                        {translation('JIT_EXPIRE_MY_RESERVATION')}
                    </PasslyButton>
                ) : (
                    <PasslyButton
                        fullWidth
                        variant='contained'
                        color='primary'
                        onClick={() => {
                            reserveUser(row.id, row.canReserve);
                        }}
                    >
                        {translation('JIT_RESERVE')}
                    </PasslyButton>
                )}
            </TableCell>
        );
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.displayName,
            label: 'TABLE_USER_TITLE',
            type: hasOrgPermission([ManageAccount]) ? 'link' : 'string',
            onClick: editRow,
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.displayName,
        },
        {
            id: TableHeadEnum.principalName,
            label: 'TABLE_PRINCIPAL_NAME_TITLE',
            type: 'string',
            filterType: FilterEnum.contains,
        },
        {
            id: TableHeadEnum.lastExpiry,
            label: 'RESERVED_UNTIL',
            type: 'getComplexCell',
            getComplexCell: getComplexLastExpiryCell,
            hideSorting: true,
        },
        {
            id: TableHeadEnum.myReservation,
            label: 'MY_RESERVED_UNTIL',
            type: 'getComplexCell',
            getComplexCell: getComplexReservationCell,
            hideSorting: true
        },
        {
            id: TableHeadEnum.lastSignedIn,
            label: 'USERS_LAST_LOGON',
            type: 'dateTime'
        },
        {
            id: TableHeadEnum.button,
            label: '',
            type: 'getComplexCell',
            getComplexCell: getComplexButtonCell
        }
    ];

    useEffect(() => {
        dispatch(showJitOrgSelect());
    }, []);

    useEffect(() => {
        setRows([]);
        dispatch(resetScroll());
    }, [filters]);

    useEffect(() => {
        // now do the data for the grid
        if (!jitUsersLoading && jitUsers) {
            let newRows = jitUsers.map((jitUser: IJitUser) => {
                const expired: boolean = rowIsExpired(jitUser.LastExpiry);

                const data: IJitUsersTableData = {
                    displayName: jitUser.DisplayName,
                    principalName: jitUser.PrincipalName,
                    lastSignedIn: jitUser.LastSignedIn,
                    lastExpiry: jitUser.LastExpiry,
                    lastExpiryIsExpired: expired,
                    requestorHasReservation: jitUser.RequestorHasReservation,
                    requestorReservationExpiry: jitUser.RequestorReservationExpiry,
                    canReserve: jitUser.CanReserve,
                    id: jitUser.Id,
                    organizationName: jitUser.OrganizationName
                };
                return data;
            });

            jitUsers.length === 0 ? setRows([]) : setRows(rows.concat(newRows));

            for (let row of newRows) {
                const expired: boolean = rowIsExpired(row.lastExpiry);

                if (expired) {
                    expireUser(row, newRows);
                }
            }
        }
    }, [jitUsersLoading, jitUsers]);

    const rowIsExpired = (expiredDate: Date) => {
        const lastExpiryDate = new Date(expiredDate);
        let expired: boolean = false;

        if (lastExpiryDate.getTime() > 0 && lastExpiryDate.getTime() < new Date().getTime()) {
            expired = true;
        }

        return expired;
    };

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        if (jitUsersLoading) {
            return;
        }

        organizationFilter && dispatch(handleGetJitUsersData(orderBy, order, infiniteScrollItemsFetchLimit, skipCount, isReset, organizationFilter, filters));
    };

    const reserveUser = (jitId: string, canReserve: boolean) => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('JIT_RESERVE_SLOT'),
            modalSwitcher: modalSwitchState,
            form: <ReserveUserDialog id={jitId} canReserve={canReserve} handleRefresh={handlePageRefreshFromDialog} />
        });
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            setRows([]);
            dispatch(resetScroll());
        }
    };

    const makeDateTimeSpanDivHelper = (dateValue: Date) => {
        return (
            <Fragment>
                <span>{passlyFormatDate(dateValue, TIME_FORMAT)}</span>
                <div>{passlyFormatDate(dateValue, DATE_FORMAT)}</div>
            </Fragment>
        );
    };

    const formatLastExpiry = (lastExpiry: Date) => {
        return `${translation('JIT_IN_USE')} (${passlyFormatDate(lastExpiry, DATE_TIME_FORMAT)})`;
    };

    const expireUser = (row: any, rows: any) => {
        jitUsersService.postRevokeMyJitReservation(row.id).then(() => {
            const rowToUpdate = rows.findIndex((rtu: IJitUsersTableData) => {
                return rtu.id === row.id;
            });

            const copyOfRowsToUpdateState = [...rows];

            copyOfRowsToUpdateState[rowToUpdate].requestorHasReservation = false;

            setRows(copyOfRowsToUpdateState);
        });
    };

    return (
        <>
            <ModalComponent {...modalData} />

            <PasslyTable
                rows={rows}
                setRows={setRows}
                order={order}
                orderBy={orderBy}
                setFilters={setFilters}
                handleOrderChange={createSortHandler}
                headerItems={headCells}
                rowsCount={jitUsersCount}
                onFetch={fetchMoreData}
                withCheckboxes={false}
                emptyListHelper={translation('EMPTY_USER_LIST')}
                loadingData={jitUsersLoading}
            />
        </>
    );
};

export default JitUsersPage;
